/**
 * Habits view component
 * Values-linked habits, streaks, reminders
 */

'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/Skeleton';
import { Target, Plus, Flame, Check, X } from 'lucide-react';
import {
  getLocalHabits,
  createLocalHabit,
  deleteLocalHabit,
  markHabitComplete,
  type LocalHabit,
} from '@/lib/storage/localHabits';

export function HabitsView() {
  const [habits, setHabits] = useState<LocalHabit[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newHabitTitle, setNewHabitTitle] = useState('');
  const [newHabitValue, setNewHabitValue] = useState('');

  useEffect(() => {
    loadHabits();
  }, []);

  const loadHabits = async () => {
    try {
      setLoading(true);
      const loadedHabits = await getLocalHabits();
      setHabits(loadedHabits);
    } catch (error) {
      console.error('Failed to load habits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHabit = async () => {
    if (!newHabitTitle.trim()) return;

    try {
      await createLocalHabit({
        title: newHabitTitle,
        value_link: newHabitValue || undefined,
      });
      setNewHabitTitle('');
      setNewHabitValue('');
      setIsDialogOpen(false);
      await loadHabits();
    } catch (error) {
      console.error('Failed to create habit:', error);
      alert('Failed to create habit. Please try again.');
    }
  };

  const handleDeleteHabit = async (habitId: string) => {
    if (!confirm('Are you sure you want to delete this habit?')) return;

    try {
      await deleteLocalHabit(habitId);
      await loadHabits();
    } catch (error) {
      console.error('Failed to delete habit:', error);
      alert('Failed to delete habit. Please try again.');
    }
  };

  const handleCompleteHabit = async (habitId: string) => {
    try {
      await markHabitComplete(habitId);
      await loadHabits();
    } catch (error) {
      console.error('Failed to mark habit complete:', error);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8 w-full">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Skeleton variant="text" className="h-10 w-64" />
          <Skeleton variant="rectangular" className="h-10 w-32" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 w-full">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2">Habit Tracker</h1>
          <p className="text-lg text-muted-foreground">
            Build values-linked habits with measurable progress and streaks
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Habit
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Habit</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="habit-title">Habit Name</Label>
                <Input
                  id="habit-title"
                  value={newHabitTitle}
                  onChange={(e) => setNewHabitTitle(e.target.value)}
                  placeholder="e.g., Morning meditation"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="habit-value">Linked Value (optional)</Label>
                <Input
                  id="habit-value"
                  value={newHabitValue}
                  onChange={(e) => setNewHabitValue(e.target.value)}
                  placeholder="e.g., Wisdom, Discipline"
                  className="mt-2"
                />
              </div>
              <Button
                onClick={handleCreateHabit}
                className="w-full"
                disabled={!newHabitTitle.trim()}
              >
                Create Habit
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </header>

      {habits.length === 0 ? (
        <Card className="p-16 text-center bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <Target className="h-16 w-16 mx-auto mb-6 text-primary/60" />
          <h3 className="text-2xl font-semibold mb-3">Start Building Your Habits</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Create your first habit to begin tracking your daily practice. Link habits to your
            values for deeper meaning.
          </p>
          <Button onClick={() => setIsDialogOpen(true)} size="lg" className="rounded-xl">
            <Plus className="h-5 w-5 mr-2" />
            Create Your First Habit
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {habits.map((habit) => (
            <Card key={habit.id} className="p-6 hover:shadow-lg transition-all">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Target className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-semibold truncate">{habit.title}</h3>
                      {habit.paused && (
                        <span className="text-xs px-2 py-1 bg-muted rounded-full mt-1 inline-block">
                          Paused
                        </span>
                      )}
                    </div>
                  </div>
                  {habit.value_link && (
                    <p className="text-sm text-muted-foreground mb-3">
                      <span className="font-medium">Linked to:</span> {habit.value_link}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <Flame className="h-5 w-5 text-orange-500" />
                    <span className="text-base font-semibold text-orange-600 dark:text-orange-400">
                      {habit.streak} day streak
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => handleCompleteHabit(habit.id)}
                    title="Mark as complete today"
                    className="rounded-lg"
                  >
                    <Check className="h-5 w-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="ghost"
                    onClick={() => handleDeleteHabit(habit.id)}
                    title="Delete habit"
                    className="rounded-lg text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
